import { ArrowUpRight } from 'lucide-react';

const WHATSAPP_NUMBER = '525584032954'; // +52 55 8403 2954
const WHATSAPP_MESSAGE =
    'Hola, quiero cotizar un envío internacional de papel grado alimenticio. Mi país es:';

/**
 * Banderas en SVG y no emoji (🇺🇸): en Windows el emoji de bandera no existe
 * — Chrome y Edge pintan las dos letras del país ("US") en vez del dibujo, y
 * buena parte de la compra B2B se hace desde una PC de escritorio. Con SVG
 * se ve igual en todos lados y además escala sin pixelarse.
 *
 * Los colores SÍ son los oficiales de cada bandera, no los de la paleta del
 * sitio: una bandera recoloreada a tonos kraft deja de ser reconocible, que
 * es justo lo único que esta pieza tiene que lograr. Lo que integra las
 * banderas al diseño es el marco de estampilla, no el color.
 */
/** Franjas de la bandera de EE. UU.: 13 en total, las impares en rojo (7
 *  rojas, empezando y terminando en rojo, como la real). Se calculan para
 *  que embonen exactas — a mano quedan huecos o traslapes de un decimal. */
const US_STRIPE = 16 / 13;
const US_RED_STRIPES = [0, 2, 4, 6, 8, 10, 12];

/** El cantón azul cubre 7 franjas de alto y 2/5 del ancho, igual que el
 *  original. */
const US_CANTON = { w: 9.6, h: 7 * US_STRIPE };

/**
 * Campo de estrellas en filas alternadas de 5 y 4, escalonadas.
 *
 * La bandera real lleva 50 estrellas en 9 filas; a este tamaño 50 puntos se
 * convierten en una mancha gris. Cuatro filas escalonadas conservan lo que
 * de verdad identifica al cantón —el damero, no el conteo— y siguen
 * leyéndose como estrellas y no como lunares.
 */
const US_STARS = Array.from({ length: 4 }, (_, row) => {
    const impar = row % 2 === 0;
    const cuantas = impar ? 5 : 4;
    return Array.from({ length: cuantas }, (_, col) => ({
        cx: impar ? (US_CANTON.w * (col + 0.5)) / 5 : (US_CANTON.w * (col + 1)) / 5,
        cy: (US_CANTON.h * (row + 0.5)) / 4,
    }));
}).flat();

const FLAGS = {
    'Estados Unidos': (
        <svg viewBox="0 0 24 16" className="block w-full h-full" aria-hidden="true">
            <rect width="24" height="16" fill="#F7F7F7" />
            {US_RED_STRIPES.map((i) => (
                <rect key={i} y={i * US_STRIPE} width="24" height={US_STRIPE} fill="#B22234" />
            ))}
            <rect width={US_CANTON.w} height={US_CANTON.h} fill="#3C3B6E" />
            {US_STARS.map((s, i) => (
                <circle key={i} cx={s.cx} cy={s.cy} r="0.42" fill="#F7F7F7" />
            ))}
        </svg>
    ),
    Canadá: (
        <svg viewBox="0 0 24 16" className="block w-full h-full" aria-hidden="true">
            <rect width="24" height="16" fill="#F7F7F7" />
            <rect width="6" height="16" fill="#D52B1E" />
            <rect x="18" width="6" height="16" fill="#D52B1E" />
            {/* Hoja de maple de once puntas con peciolo, no una estrella
                genérica: es lo único que distingue esta bandera de la de
                Perú, que también es roja-blanca-roja vertical. */}
            <path
                d="M12 3.5l1.02 1.9c.2.36.56.3.92.1l1.3-.67-.44 2.3c-.07.4.1.5.36.55l.9.16-1.6 1.4c-.24.2-.17.4-.08.65l.3.9-2.6-.5c-.3-.06-.44.06-.46.28l-.13 2.55h-.64l-.13-2.55c-.02-.22-.16-.34-.46-.28l-2.6.5.3-.9c.09-.25.16-.45-.08-.65l-1.6-1.4.9-.16c.26-.05.43-.15.36-.55l-.44-2.3 1.3.67c.36.2.72.26.92-.1z"
                fill="#D52B1E"
            />
        </svg>
    ),
    Guatemala: (
        <svg viewBox="0 0 24 16" className="block w-full h-full" aria-hidden="true">
            <rect width="24" height="16" fill="#F7F7F7" />
            <rect width="8" height="16" fill="#4997D0" />
            <rect x="16" width="8" height="16" fill="#4997D0" />
        </svg>
    ),
};

function FlagStamp({ country, tilt }) {
    return (
        <figure className="flex flex-col items-center gap-2.5">
            {/* El borde punteado es la perforación de la estampilla: el mismo
                recurso que el matasellos del sitio ya usa en su círculo
                exterior (strokeDasharray con puntas redondas). */}
            <span
                className="stamp-chip"
                style={{ '--stamp-tilt': `${tilt}deg` }}
            >
                {/* 56×38 y no 44×30: por debajo de este tamaño el campo de
                    estrellas y la hoja de maple se vuelven manchas y las tres
                    banderas empiezan a parecerse entre sí. */}
                <span className="block w-14 h-[38px] overflow-hidden rounded-[1px]">
                    {FLAGS[country]}
                </span>
            </span>
            <figcaption
                className="font-mono text-[9px] uppercase tracking-[0.14em] text-center"
                style={{ color: 'var(--ink-soft)' }}
            >
                {country}
            </figcaption>
        </figure>
    );
}

/**
 * Banda de cobertura: dice que Carpepel también envía fuera de México.
 *
 * El canto de correo aéreo (las franjas diagonales) no es adorno: es el
 * código visual universal del envío internacional, y encaja con el lenguaje
 * postal que el sitio ya venía hablando (matasellos, perforado, papel
 * rasgado) en vez de meter un globo terráqueo genérico que no pertenece a
 * esta paleta.
 *
 * El botón lleva a WhatsApp y NO al checkout a propósito: el cotizador de
 * envíos valida código postal mexicano de 5 dígitos y manda país "MX" fijo
 * (backend/server.py), así que un pedido internacional no puede completarse
 * ahí. Mandarlo al checkout sería prometer algo que la página no puede
 * cumplir, justo en el paso donde más caro sale fallar.
 */
export default function ShippingCoverage() {
    const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

    return (
        <section data-testid="shipping-coverage" style={{ backgroundColor: 'var(--paper-2)' }}>
            <div className="airmail-edge" aria-hidden="true" />

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
                    <div className="md:col-span-6">
                        <p className="eyebrow mb-6">Cobertura</p>

                        <h2
                            className="font-heading font-black tracking-tight text-balance mb-5"
                            style={{ color: 'var(--ink)', fontSize: 'clamp(1.75rem, 3.4vw, 2.75rem)', lineHeight: 1.02 }}
                        >
                            No nos detenemos en la frontera
                        </h2>

                        <p className="text-sm sm:text-base leading-relaxed mb-7 max-w-[52ch]" style={{ color: 'var(--ink-soft)' }}>
                            Surtimos a toda la República Mexicana con paqueterías nacionales, y
                            también enviamos a Estados Unidos, Canadá y Centroamérica. Los envíos
                            internacionales se cotizan uno por uno: el costo depende del país, del
                            peso de la paca y del trámite de aduana, así que lo vemos contigo por
                            WhatsApp antes de que pagues nada.
                        </p>

                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="coverage-whatsapp-cta"
                            className="group inline-flex items-center gap-2 h-12 px-7 rounded-sm uppercase font-bold tracking-widest text-xs transition-colors duration-200"
                            style={{ backgroundColor: 'var(--ink)', color: 'var(--paper)' }}
                        >
                            Cotizar envío internacional
                            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>

                    <div className="md:col-span-6 md:pl-6">
                        <div className="flex flex-wrap items-start gap-7 sm:gap-9">
                            <FlagStamp country="Estados Unidos" tilt={-2.5} />
                            <FlagStamp country="Canadá" tilt={1.5} />
                            <FlagStamp country="Guatemala" tilt={-1} />
                        </div>
                        <p
                            className="font-mono text-[10px] uppercase tracking-[0.16em] mt-7 leading-relaxed"
                            style={{ color: 'var(--ink-soft)' }}
                        >
                            Y el resto de Centroamérica · Consulta tu país
                        </p>
                    </div>
                </div>
            </div>

            <div className="airmail-edge" aria-hidden="true" />
        </section>
    );
}
